<template>
    <div :data-type="type" class="form-item-rating-input"
         :class="classRating">
        <template v-for="option in options" :key="option.value">
            <input
                :id="option.id"
                type="radio"
                :name="name"
                :value="option.value"
                :checked="isRatingSelected(option.value)"
                @change="handleRatingChange(option.value)" />

            <label
                :for="option.id"
                class="rating-label"
                :title="`Calificación ${option.value}`"
                @click="handleRatingChange(option.value)">
            </label>
        </template>
    </div>
    <div v-if="shouldShowError" class="has-error-msg invalid-feedback d-block">
        {{ errorMessage }}
    </div>
</template>

<script setup>
import { computed, watch, ref, onMounted } from 'vue';
import { useField } from 'vee-validate';
import { useTranslation } from '@/composables/useTranslation';

// INITIALIZE STORE & COMPOSABLES
const { translate } = useTranslation();

// PROPS & EMITS
const props = defineProps({
    classRating: {
        type: String,
        required: false,
    },
    errorText: {
        type: String,
        required: false,
    },
    fieldId: {
        type: Number,
        required: true,
    },
    name: {
        type: String,
        required: true,
    },
    options: {
        type: Array,
        required: true,
    },
    question: {
        type: Object,
        required: true,
    },
    type: {
        type: String,
        required: true,
    },
    validation: {
        type: Object,
        required: false,
    },
    hasSubmitted: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelAnswer', 'register-field', 'field-change']);

// REACTIVE VARIABLES
// Estado local para manejar la selección
const selectedValue = ref(null);

// COMPUTED PROPERTIES
// Crear reglas de validación dinámicas
const validationRules = computed(() => {
    return (value) => {
        if (props.validation.required && (!value || value === null)) {
            return props.errorText || translate('label.this_value_is_required');
        }

        return true;
    };
});

// METHODS
// Solo mostrar errores después del primer submit
const shouldShowError = computed(() => {
    return props.hasSubmitted && errorMessage.value;
});

// Manejar cambios en la calificación
const handleRatingChange = (ratingValue) => {
    selectedValue.value = ratingValue;
    value.value = ratingValue;
    emit('update:modelAnswer', ratingValue);
    emit('field-change', props.name, ratingValue);
};

// Verificar si una calificación está seleccionada
const isRatingSelected = (ratingValue) => {
    return selectedValue.value === ratingValue;
};

// Configurar useField con validación lazy
const { value, errorMessage, meta } = useField(props.name, validationRules, {
    initialValue: props.question.answer || null,
    validateOnValueUpdate: false,
    validateOnMount: false,
});

// LIFECYCLE HOOKS
// Sincronizar con el valor externo
watch(() => props.question.answer, (newValue) => {
    selectedValue.value = newValue || null;
    value.value = newValue || null;
}, { immediate: true });

// Registrar campo al montar
onMounted(() => {
    emit('register-field', props.name, selectedValue.value);
});
</script>
